import type { FC, ReactNode } from 'react';
import { useCallback, useEffect, useEffectEvent, useMemo, useState } from 'react';

import { useLocation, useSearchParams } from 'react-router';

import { SHOW_SELECTED_URL_PARAM } from '@/config/constants';
import { useAppStore } from '@/store/hooks';

import { SORT_PARAM } from '../hooks/useCatalogSorting';
import { extractFiltersFromParams } from '../lib/extractFiltersFromParams';
import { getEmptyFilterState } from '../lib/getEmptyFilterState';
import type { FilterState } from '../types/filter.types';

import { FilterContext } from './FilterContextBase';

export const FilterProvider: FC<{
  children: ReactNode;
  disableSearchParams?: boolean;
}> = ({ children, disableSearchParams = false }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Local state used only when disableSearchParams=true (e.g. modal — no URL interaction)
  const [localFilters, setLocalFilters] = useState<FilterState>(getEmptyFilterState);

  // URL is the source of truth for the main page; local state for the modal
  const filters: FilterState = useMemo(
    () => (disableSearchParams ? localFilters : extractFiltersFromParams(searchParams)),
    [disableSearchParams, localFilters, searchParams]
  );

  const store = useAppStore();
  const location = useLocation();
  const showSelectedFilter = filters[SHOW_SELECTED_URL_PARAM];

  const clearShowSelectedFilter = useEffectEvent(() => {
    if (disableSearchParams) {
      setLocalFilters(prev => ({ ...prev, [SHOW_SELECTED_URL_PARAM]: false }));
    } else {
      setSearchParams(params => {
        const next = new URLSearchParams(params);
        next.delete(SHOW_SELECTED_URL_PARAM);
        return next;
      });
    }
  });

  const clearShowSelectedIfEmpty = useEffectEvent(() => {
    const isHaveEmptySymbols = store.getState().symbolSelector.symbols.length === 0;
    if (
      (location.pathname.includes('catalog') || location.pathname.includes('dashboard')) &&
      isHaveEmptySymbols &&
      showSelectedFilter
    ) {
      clearShowSelectedFilter();
    }
  });

  useEffect(() => {
    // oxlint-disable-next-line react-hooks-js/set-state-in-effect -- Keep URL/local filter state in sync when navigating with no symbols selected.
    clearShowSelectedIfEmpty();
  }, [location.pathname]);

  const setFilter = useCallback(
    (key: string, value: string | null | boolean) => {
      const currentValue = filters[key as keyof FilterState];
      const isToggling =
        key !== 'search' && value !== null && String(value) === String(currentValue);
      const newValue = isToggling ? null : value;

      if (disableSearchParams) {
        setLocalFilters(prev => ({ ...prev, [key]: newValue }));
      } else {
        setSearchParams(params => {
          const next = new URLSearchParams(params);
          next.delete(key);
          if (newValue !== null && newValue !== '') {
            next.set(key, String(newValue));
          }
          if (key === 'search' && newValue !== currentValue) {
            next.delete(SORT_PARAM);
          }
          return next;
        });
      }
    },
    [filters, setSearchParams, disableSearchParams]
  );

  const toggleFilterOption = useCallback(
    (key: string, value: string) => {
      if (disableSearchParams) {
        setLocalFilters(prev => {
          const current = (prev[key as keyof FilterState] as string[] | undefined) ?? [];
          const next = current.includes(value)
            ? current.filter(option => option !== value)
            : [...current, value];
          return { ...prev, [key]: next };
        });
      } else {
        setSearchParams(params => {
          const next = new URLSearchParams(params);
          const current = next.getAll(key);
          next.delete(key);
          const updated = current.includes(value)
            ? current.filter(option => option !== value)
            : [...current, value];
          for (const option of updated) next.append(key, option);
          return next;
        });
      }
    },
    [setSearchParams, disableSearchParams]
  );

  const clearAllFilters = useCallback(() => {
    if (disableSearchParams) {
      setLocalFilters(getEmptyFilterState());
    } else {
      setSearchParams({}, { replace: true });
    }
  }, [setSearchParams, disableSearchParams]);

  const isAnyFilterApplied = useMemo(() => {
    const entries = Object.entries(filters).filter(
      ([key]) => key !== SHOW_SELECTED_URL_PARAM && key !== 'search'
    );
    return !entries.every(([, value]) =>
      Array.isArray(value) ? value.length === 0 : value === null || value === false
    );
  }, [filters]);

  const value = useMemo(
    () => ({
      filters,
      clearAllFilters,
      setFilter,
      toggleFilterOption,
      isAnyFilterApplied,
      disableSearchParams
    }),
    [
      filters,
      clearAllFilters,
      setFilter,
      toggleFilterOption,
      isAnyFilterApplied,
      disableSearchParams
    ]
  );

  return <FilterContext value={value}>{children}</FilterContext>;
};
